import type { Period } from './types';

/**
 * Datas do domínio. Tudo trabalha com texto ISO em horário local (`yyyy-mm-dd`), sem
 * fuso: um culto às 19h de domingo é "domingo, 19h" para quem está na igreja, e
 * converter para UTC só criaria erro de um dia nas bordas.
 */

export const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;
export const WEEKDAY_LONG = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const;
export const MONTH_LONG = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;
export const MONTH_SHORT = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;
export const PERIOD_LABEL = ['Manhã', 'Tarde', 'Noite'] as const;
export const PERIOD_HINT = ['até 12h', '12h – 18h', 'após 18h'] as const;

export function parseDate(iso: string): Date {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);

  return new Date(y, m - 1, d);
}

export function toISODate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Hoje, em horário local. Lido na chamada, para os testes poderem fixar o relógio. */
export function todayISO(): string {
  return toISODate(new Date());
}

export function addDays(iso: string, days: number): string {
  const date = parseDate(iso);
  date.setDate(date.getDate() + days);

  return toISODate(date);
}

/** Dias de `from` até `to` (negativo quando `to` vem antes). */
export function diffDays(from: string, to: string): number {
  return Math.round((parseDate(to).getTime() - parseDate(from).getTime()) / 86_400_000);
}

export function weekdayOf(iso: string): number {
  return parseDate(iso).getDay();
}

/** Domingo da semana da data. */
export function startOfWeek(iso: string): string {
  return addDays(iso, -weekdayOf(iso));
}

export function periodOf(time: string): Period {
  const hour = Number(time.slice(0, 2));

  if (hour < 12) return 0;

  return hour < 18 ? 1 : 2;
}

/** "12 out" */
export function formatDayMonth(iso: string): string {
  const date = parseDate(iso);

  return `${date.getDate()} ${MONTH_SHORT[date.getMonth()]}`;
}

/** "Dom, 12 out" */
export function formatShortDate(iso: string): string {
  return `${WEEKDAY_SHORT[weekdayOf(iso)]}, ${formatDayMonth(iso)}`;
}

/** "Domingo, 12 de outubro" */
export function formatLongDate(iso: string): string {
  const date = parseDate(iso);

  return `${WEEKDAY_LONG[date.getDay()]}, ${date.getDate()} de ${MONTH_LONG[date.getMonth()]}`;
}

/** "12/10/2026" */
export function formatNumericDate(iso: string): string {
  const [y, m, d] = iso.split('-');

  return `${d}/${m}/${y}`;
}

/** "Hoje", "Amanhã", "Em 3 dias", "Há 2 dias" — relativo a `today`. */
export function relativeDay(iso: string, today: string = todayISO()): string {
  const n = diffDays(today, iso);

  if (n === 0) return 'Hoje';
  if (n === 1) return 'Amanhã';
  if (n > 1) return `Em ${n} dias`;

  return `Há ${-n} ${n === -1 ? 'dia' : 'dias'}`;
}

/** "há 5 min", "há 2 h", "há 3 dias" — para o histórico e as notificações. */
export function relativeTime(at: number, now: number = Date.now()): string {
  const minutes = Math.round((now - at) / 60_000);

  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  if (minutes < 1440) return `há ${Math.round(minutes / 60)} h`;

  const days = Math.round(minutes / 1440);

  return `há ${days} ${days > 1 ? 'dias' : 'dia'}`;
}

/** Idade completa em anos na data de referência, ou `null` sem data válida. */
export function ageOf(birthDate: string | null, today: string = todayISO()): number | null {
  if (!birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) {
    return null;
  }

  const [by = 0, bm = 0, bd = 0] = birthDate.split('-').map(Number);
  const [ty = 0, tm = 0, td = 0] = today.split('-').map(Number);
  let age = ty - by;

  if (tm < bm || (tm === bm && td < bd)) {
    age--;
  }

  return age >= 0 ? age : null;
}

/** "2026-10" de uma data ISO. */
export function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

/** Normaliza para busca: sem acento, sem caixa. */
export function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** Hash estável de texto — usado para cor de avatar e desempate determinístico. */
export function hashString(text: string): number {
  let h = 7;

  for (const char of text) {
    h = (Math.imul(h, 31) + char.charCodeAt(0)) >>> 0;
  }

  return h;
}
